/* Core & libraries */
import React from "react";
import { FormattedMessage } from "react-intl";
import axios from "axios";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import { useNotificationContext } from "../contexts/notificationContext";
import {
  CHANGE_COMPANY_ENDPOINT,
  GET_COUNTRIES_ENDPOINT, GET_LABELS_ENDPOINT,
  GET_PHONE_CODES_ENDPOINT,
  LOGOUT_ENDPOINT,
  STATES_ENDPOINT,
  UC_DASH_SAML_LOGIN,
} from '../endpoints/endpoints';
import { GET_ROLES_ENDPOINT, GET_TEAMS_ENDPOINT, SET_TRANSLATION_ENDPOINT, GET_MAINTENANCE_ENDPOINT, GET_AA_REGISTRATION_COUNTRIES_ENDPOINT } from "../endpoints/endpoints";
import { useUserContext } from "../contexts/userContext";
import { API_HOST, API_PROTOCOL } from "../constants/appConstants";
import { useNavigate }  from "react-router";
import { APP_PATH } from "../constants/routes";
import { useUserService } from "./userService";
import { useGlobalContext, useGlobalUpdateContext } from "../contexts/globalContext";

/* Components */

/* Styles */

export const useGeneralService = (props = {}) => {
  const { onError } = props
  const { genericApiFetch } = useGenericApiFetch();
  const userContext = useUserContext();
  const { setNotification } = useNotificationContext();
  const navigate = useNavigate();
  const { getUserDetails } = useUserService();
  const globalData = useGlobalContext();
  const setGlobalData = useGlobalUpdateContext();
  const providerCode = userContext?.userInfo?.providerInfo?.providerCode;

  const getRoles = async () => {
    const response = await genericApiFetch({
      url: GET_ROLES_ENDPOINT,
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    setNotification(
      "refresh",
      <FormattedMessage id="notifications.genericTitle" />,
      <FormattedMessage id="notifications.genericDescription" />
    );
    return false
  };

  const getTeams = async () => {
    const response = await genericApiFetch({
      url: GET_TEAMS_ENDPOINT.replace("{companyEntityId}", userContext?.userInfo?.companyInfo?.companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    setNotification(
      "refresh",
      <FormattedMessage id="notifications.genericTitle" />,
      <FormattedMessage id="notifications.genericDescription" />
    );
    return false
  };

  const getStates = async (countryCode) => {
    const response = await genericApiFetch({
      url: STATES_ENDPOINT.replace('{countryCode}', countryCode),
      method: 'GET',
    });
    if (response.success) {
      return response.data;
    }
    setNotification(
      'error',
      <FormattedMessage id='states.notificationTitle' />,
      <FormattedMessage id='states.notificationText' />
    );
    return false;
  };

  const setTranslation = async (locale) => {
    // lang initally saved only for logged in users, commenting this so it saves the locale set on login.
    // if (!userContext?.loggedIn) {
    //   return true;
    // }

    const response = await genericApiFetch({
      url: SET_TRANSLATION_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace('{locale}', locale),
      method: "GET",
      spinner: "localeSpinner"
    });

    return !!response.success;
  }

  const logout = async () => {

    const REDIRECT_PATH = API_PROTOCOL + "://" + API_HOST;
    const response = await genericApiFetch({
      url: LOGOUT_ENDPOINT,
      method: 'GET',
    });
    if (response.success && response.data.ssoLogoutUrl) {
      await axios
        .get(response.data.ssoLogoutUrl)
        .catch((error) => {
          console.log(error);
        });
      window.location.href = REDIRECT_PATH;
    } else {
      setNotification(
        'error',
        <FormattedMessage id='travelerRegistration.notificationTitle' />,
        <FormattedMessage id='travelerRegistration.notificationText' />
      );
    }
    return !!response.success;
  };

  const changeCompany = async (payload) => {

    const response = await genericApiFetch({
      url: CHANGE_COMPANY_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()),
      method: "POST",
      data : payload,
      spinner: "companiesSwitcherSpinner"
    });

    if (response.success) {
      setGlobalData({
        ...globalData,
        programDetails: {}
      })
      await getUserDetails();
      return navigate(APP_PATH);
    }
    setNotification(
      'error',
      <FormattedMessage id='notifications.companySwitchErrorTitle' />,
      <FormattedMessage id='notifications.companySwitchErrorText' />
    );
    return !!response.success;
  }

  const getMaintenanceData = async (options) => {
    const response = await genericApiFetch({
      url: GET_MAINTENANCE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()),
      queryParams: options,
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false
  };

  const UcLoginSaml = async (payload) => {
    const response = await genericApiFetch({
      url: UC_DASH_SAML_LOGIN,
      method: "POST",
      data: payload,
      spinner: "ucLoginSamlSpinner",
    });

    if (response.success && !response.data?.errors && !!response.data?.ssoLoginUrl) {
      window.location.href = response.data?.ssoLoginUrl
      return true;
    } else {
      if (typeof onError === "function") {
        onError();
      }
    }
    return false;
  };

  const getCountries = async () => {
    const response = await genericApiFetch({
      url: GET_COUNTRIES_ENDPOINT,
      method: "GET",
    });
    if (response.success) {
      return response?.data?.countries;
    }
    setNotification(
      "danger",
      <FormattedMessage id="companyRegistration.notificationTitle" />,
      <FormattedMessage id="companyRegistration.notificationText" />
    );
    return false;
  };

  const getAARegistrationCountries = async () => {
    const response = await genericApiFetch({
      url: GET_AA_REGISTRATION_COUNTRIES_ENDPOINT,
      method: "GET",
    });
    if (response.success) {
      return response?.data?.countries;
    }
    setNotification(
      "danger",
      <FormattedMessage id="companyRegistration.notificationTitle" />,
      <FormattedMessage id="companyRegistration.notificationText" />
    );
    return false;
  };

  const getPhoneCodes = async () => {
    const response = await genericApiFetch({
      url: GET_PHONE_CODES_ENDPOINT,
      method: "GET",
    });
    if (response.success) {
      return response?.data?.list;
    }
    setNotification(
      "danger",
      <FormattedMessage id="companyRegistration.notificationTitle" />,
      <FormattedMessage id="companyRegistration.notificationText" />
    );
    return false;
  };

  return {
    getRoles,
    getTeams,
    getStates,
    setTranslation,
    logout,
    changeCompany,
    getMaintenanceData,
    UcLoginSaml,
    getCountries,
    getPhoneCodes,
    getAARegistrationCountries
  };
}

